import { useMemo, useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import type { AICheckerStats, AICheckerAllTime } from '../../../../../services/admin/adminService';
import { ReviewSession, ScoreRing, type ReviewBucket } from '../../AuditReview';
import type { TFn } from '../types';

/**
 * AI-cost breakdown card: the real "order processing" spend (LLM extraction + category assignment),
 * the "AI checks" spend (the AI Checker's verify/audit/triage re-checks), and their combined total —
 * for the selected range, with a stacked bar showing the split and the all-time combined total below.
 */
function AICostCard({ stats, allTime, t }: { stats: AICheckerStats; allTime: AICheckerAllTime; t: TFn }) {
  const processing = stats.processingCostEur;
  const checks = stats.aiCostEur;
  const combined = processing + checks;
  const pct = (v: number) => (combined > 0 ? (v / combined) * 100 : 0);
  const allTimeCombined = allTime.processingCostEur + allTime.costEur;

  return (
    <div className="flex flex-col h-full">
      <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>{t('adminTesting.review.costHeading')}</div>
      <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4">
        {/* Combined headline */}
        <div className={cn('text-3xl font-bold tabular-nums leading-none', primaryText)}>€{combined.toFixed(4)}</div>
        <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mt-1')}>
          {t('adminTesting.review.costAllTimeSub', { cost: allTimeCombined.toFixed(4), count: allTime.checked })}
        </div>

        {/* Stacked split bar */}
        <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div className="h-full bg-indigo-500 dark:bg-indigo-400" style={{ width: `${pct(processing)}%` }} title={`${t('adminTesting.review.costProcessing')}: €${processing.toFixed(4)}`} />
          <div className="h-full bg-amber-500 dark:bg-amber-400" style={{ width: `${pct(checks)}%` }} title={`${t('adminTesting.review.costChecks')}: €${checks.toFixed(4)}`} />
        </div>

        {/* Two components — dot · label … value */}
        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className={cn('flex items-center gap-1.5 text-xs', secondaryText)}>
              <span className="inline-block h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-400" />
              {t('adminTesting.review.costProcessing')}
            </span>
            <span className={cn('text-xs tabular-nums', semiBoldText, 'text-indigo-600 dark:text-indigo-400')}>€{processing.toFixed(4)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className={cn('flex items-center gap-1.5 text-xs', secondaryText)}>
              <span className="inline-block h-2 w-2 rounded-full bg-amber-500 dark:bg-amber-400" />
              {t('adminTesting.review.costChecks')}
            </span>
            <span className={cn('text-xs tabular-nums', semiBoldText, 'text-amber-600 dark:text-amber-400')}>€{checks.toFixed(4)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * AI Checker hero — the at-a-glance block: which period, the data-health score, total spend,
 * and the "needs attention" callout with the open work items. Everything else (overview tiles,
 * issue-type breakdown) is secondary context below. The review walk-through is demoted to a small
 * launcher button so the focus stays on the numbers.
 */
export function AICheckerHero({
  t, periodLabel, accountLabel, scorePct, stats, allTime, emailIssuesOpen, quantityFlagsOpen, reviewBuckets,
  emailsPerHour, onFocusNeedsAttention,
}: {
  t: TFn; periodLabel: string; accountLabel: string; scorePct: number; stats: AICheckerStats; allTime: AICheckerAllTime;
  emailIssuesOpen: number; quantityFlagsOpen: number; reviewBuckets: ReviewBucket[];
  emailsPerHour: number; onFocusNeedsAttention: () => void;
}) {
  const liveBuckets = useMemo(() => reviewBuckets.filter(b => b.items.length > 0), [reviewBuckets]);
  const toHandle = useMemo(() => liveBuckets.reduce((n, b) => n + b.items.length, 0), [liveBuckets]);
  const [sessionOpen, setSessionOpen] = useState(false);
  // "Open" = everything still to tick off: open email issues + open quantity flags.
  const openTotal = emailIssuesOpen + quantityFlagsOpen;
  const allClear = openTotal === 0;

  return (
    <div className="space-y-4">
      {/* Period header — what window are we looking at, live throughput, + review launcher */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{accountLabel}</div>
            <h3 className={cn(primaryText, 'text-xl font-bold mt-0.5')}>{periodLabel}</h3>
          </div>
          {/* Live throughput pill — the "well-oiled machine" pulse */}
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/15 px-2.5 py-1">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 dark:bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500 dark:bg-emerald-400" />
            </span>
            <span className={cn('text-[11px] uppercase tracking-wide font-semibold text-emerald-700 dark:text-emerald-300')}>{t('adminTesting.review.liveLabel')}</span>
            <span className={cn('text-xs font-bold tabular-nums text-emerald-700 dark:text-emerald-300')}>{emailsPerHour.toFixed(1)} {t('adminTesting.review.perHourUnit')}</span>
          </span>
        </div>
        {toHandle > 0 && (
          <button
            onClick={() => setSessionOpen(true)}
            className={cn('px-3 py-1.5 rounded-lg border border-purple-300 dark:border-purple-900/50 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors', smallTextSize, semiBoldText)}
          >
            {t('adminTesting.review.startReview')} ({toHandle}) →
          </button>
        )}
      </div>

      {/* Key data — score + needs attention on the left, spend breakdown on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
        {/* Score */}
        <div className="lg:col-span-3 flex flex-col">
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2')}>{t('adminTesting.review.heroScoreHeading')}</div>
          <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-4 flex items-center gap-4">
            <ScoreRing scorePct={scorePct} />
            <div className="min-w-0">
              <div className={cn('text-2xl font-bold tabular-nums', primaryText)}>{stats.total}</div>
              <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide')}>{t('adminTesting.review.statChecked')}</div>
            </div>
          </div>
        </div>

        {/* Needs attention — the only number that must reach 0; click to jump to the to-do list */}
        <div className="lg:col-span-4 flex flex-col">
          <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mb-2 flex items-center gap-1.5')}>
            {allClear ? '✓' : '⚠'} {t('adminTesting.review.kpiFocusHeading')}
          </div>
          <button
            type="button"
            onClick={allClear ? undefined : onFocusNeedsAttention}
            disabled={allClear}
            className={cn(
              'group flex-1 text-left rounded-xl border p-4 transition-colors',
              allClear
                ? 'cursor-default border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/10'
                : 'border-red-200 dark:border-red-900/40 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/15 dark:to-orange-900/10 hover:border-red-300 dark:hover:border-red-800 hover:shadow-sm'
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={cn('text-3xl font-bold tabular-nums leading-none', openTotal > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
                  {openTotal}
                </div>
                <div className={cn(secondaryText, 'text-[10px] uppercase tracking-wide mt-1')}>
                  {allClear ? t('adminTesting.review.kpiAllClearTitle') : t('adminTesting.review.statOpenSub')}
                </div>
              </div>
              {!allClear && (
                <span className={cn('text-xs font-semibold text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap')}>
                  {t('adminTesting.review.kpiReviewCta')} →
                </span>
              )}
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className={cn('flex items-center gap-1.5 text-xs', secondaryText)}>
                  <span className={cn('inline-block h-2 w-2 rounded-full', emailIssuesOpen > 0 ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600')} />
                  {t('adminTesting.review.statEmailIssues')}
                </span>
                <span className={cn('text-xs tabular-nums', semiBoldText, emailIssuesOpen > 0 ? 'text-red-600 dark:text-red-400' : primaryText)}>{emailIssuesOpen}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className={cn('flex items-center gap-1.5 text-xs', secondaryText)}>
                  <span className={cn('inline-block h-2 w-2 rounded-full', quantityFlagsOpen > 0 ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600')} />
                  {t('adminTesting.review.statQuantity')}
                </span>
                <span className={cn('text-xs tabular-nums', semiBoldText, quantityFlagsOpen > 0 ? 'text-orange-600 dark:text-orange-400' : primaryText)}>{quantityFlagsOpen}</span>
              </div>
            </div>
          </button>
        </div>

        {/* Total spent (range) + breakdown */}
        <div className="lg:col-span-5 flex flex-col">
          <AICostCard stats={stats} allTime={allTime} t={t} />
        </div>
      </div>

      {sessionOpen && (
        <ReviewSession title={t('adminTesting.review.scoreTitleAiChecker', { account: accountLabel })} buckets={liveBuckets} onClose={() => setSessionOpen(false)} t={t} />
      )}
    </div>
  );
}
